import { Component, HostListener, Input, Output, EventEmitter, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash';
import { AccordionModule, Accordion } from 'primeng/accordion';
import { select, Store } from '@ngrx/store';
import { take, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TranslateModule } from '@ngx-translate/core';
import { SelectModule, Select } from 'primeng/select';

import * as fromStore from 'src/app/_store/_reducers';
import { AuthService } from 'src/app/_shared/services';
import { FiltersPrefValue } from 'src/app/_shared/interface/filters-pref.interface';
import { UserPreferences } from 'src/app/_shared/interface/user-preferences.interface';
import { StateFilter } from 'src/app/_shared/interface/state-filter.interface';
import { OnDestroyMixin, untilComponentDestroyed } from 'src/app/_shared/classes/component-destroy.class';
import { StringFilter } from 'src/app/_shared/interface/string-filter.interface';
import { StatusFilter } from 'src/app/_shared/interface/status-filter.interface';
import { IconRegistryService } from '@powerstandards/powerside-ui-lib';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    NgTemplateOutlet,
    AccordionModule,
    InputTextModule,
    FormsModule,
    MultiSelectModule,
    SelectModule,
    ButtonModule,
    FloatLabelModule,
    TranslateModule,
    NgClass,
  ],
})
export class FilterComponent extends OnDestroyMixin implements OnDestroy, OnInit {
  @Input() from: string;
  @Input() searchFilter: string;
  @Input() searchHistory: Array<string> = [];
  @Input() locationFilter: string;
  @Input() stateOptions: Array<StateFilter>;
  @Input() stateFilter: Array<StateFilter>;
  @Input() statusOptions: Array<StatusFilter>;
  @Input() statusFilter: Array<StatusFilter>;
  @Input() alarmOptions: Array<StringFilter>;
  @Input() alarmFilter: Array<string>;
  @Input() commsOptions: Array<StringFilter>;
  @Input() commsFilter: Array<string>;
  @Input() search: boolean;
  @Input() state: boolean;
  @Input() status: boolean;
  @Input() alarm: boolean;
  @Input() comms: boolean;
  @Input() clearButton: boolean;
  @Input() disable: boolean;
  @Input() userPreferenceName: string;

  @Output() searchFilterChange = new EventEmitter<string>();
  @Output() stateFilterChange = new EventEmitter<Array<StateFilter>>();
  @Output() statusFilterChange = new EventEmitter<Array<StatusFilter>>();
  @Output() alarmFilterChange = new EventEmitter<Array<string>>();
  @Output() commsFilterChange = new EventEmitter<Array<string>>();
  @Output() clearFilters = new EventEmitter<boolean>();

  @ViewChild('comboFilter') comboFilter: Accordion;
  @ViewChild('searchSelect') searchSelect: Select;

  smallScreen: boolean;
  offsetSmallScreen: number;
  maxHistoryItems = 5;
  iconService: IconRegistryService;

  private searchDebounceTimer: ReturnType<typeof setTimeout>;

  @HostListener('window:resize')
  onResize(): void {
    this.smallScreen = window.innerWidth < this.offsetSmallScreen;
  }

  constructor(
    private store: Store<fromStore.State>,
    private authService: AuthService,
    private iconRegistryService: IconRegistryService
  ) {
    super();
    this.iconService = iconRegistryService;

    // Automatically save filters after 2 seconds of inactivity
    merge(
      this.searchFilterChange,
      this.stateFilterChange,
      this.statusFilterChange,
      this.alarmFilterChange,
      this.commsFilterChange,
      this.clearFilters
    )
      .pipe(debounceTime(2000), untilComponentDestroyed(this))
      .subscribe((_value) => {
        this.saveFilters();
      });
  }

  ngOnInit(): void {
    this.offsetSmallScreen = Math.max(
      [this.search, this.state, this.status, this.alarm, this.comms].filter(Boolean).length * 265 + 250,
      991
    );
    this.onResize();
    this.clearFilters.pipe(untilComponentDestroyed(this)).subscribe(() => {
      if (this.userPreferenceName) {
        this.store
          .pipe(select(fromStore.getUserPreferences))
          .pipe(take(1))
          .subscribe((userPrefs) => {
            let userPrefsClone: UserPreferences = cloneDeep(userPrefs);
            userPrefsClone.filters[this.userPreferenceName] = {};
            if (userPrefsClone !== userPrefs) {
              this.authService.savePreferences({ ...userPrefsClone }, true);
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    // Clear any pending debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.saveFilters();

    super.ngOnDestroy();
  }

  saveFilters(): void {
    if (this.userPreferenceName) {
      this.store
        .pipe(select(fromStore.getUserPreferences))
        .pipe(take(1))
        .subscribe((userPrefs) => {
          let userPrefsClone: UserPreferences = cloneDeep(userPrefs);
          let filterPrefs: FiltersPrefValue = {};
          let kpiFilters = {};

          if (this.state) {
            filterPrefs.stateFilter = this.stateFilter;
          }

          if (this.status) {
            filterPrefs.statusFilter = this.statusFilter;
          }

          if (this.alarm) {
            kpiFilters = { kpiFilters: this.alarmFilter };
          }

          if (this.comms) {
            filterPrefs.commsFilter = this.commsFilter;
          }

          filterPrefs.searchFilter = this.searchFilter;

          if (!userPrefsClone.filters) {
            userPrefsClone.filters = {};
          }
          filterPrefs.searchHistory = this.searchHistory;
          userPrefsClone.filters[this.userPreferenceName] = filterPrefs;

          if (userPrefsClone !== userPrefs) {
            this.authService.savePreferences({ ...userPrefsClone, ...kpiFilters }, true);
          }
        });
    }
  }

  onSearchFocus(): void {
    if (this.searchHistory?.length > 0) {
      this.searchSelect?.show(true);
    }
  }

  onClear(): void {
    var test;
  }

  onSearchChange(value: string): void {
    this.searchFilterChange.emit(value);

    // Clear existing timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (value && value.trim().length > 0) {
      this.searchDebounceTimer = setTimeout(() => {
        this.addToSearchHistory(value.trim());
        this.searchSelect?.hide(false);
      }, 500);
    }
  }

  private addToSearchHistory(searchTerm: string): void {
    // Create a new array to avoid mutating frozen/sealed array from store
    let updatedHistory = [...this.searchHistory];

    const index = updatedHistory.indexOf(searchTerm);
    if (index > -1) {
      updatedHistory.splice(index, 1);
    }

    updatedHistory.unshift(searchTerm);

    if (updatedHistory.length > this.maxHistoryItems) {
      updatedHistory.pop();
    }

    this.searchHistory = updatedHistory;
  }
}
